/**
 * Token Manager
 * Manages temporary tokens during signup/signin flow.
 *
 * SECURITY NOTE:
 * Uses sessionStorage to persist state across page reloads (tab-specific).
 * While less secure than in-memory storage (vulnerable to XSS), it is required
 * for UX (surviving refresh). Backend tokens MUST have short expiration times.
 */

import { STORAGE_KEYS } from "@monorepo/config";

// Configuration map to shared storage keys
const KEYS = {
  PRE_SIGNUP_TOKEN: STORAGE_KEYS.PRE_SIGNUP_TOKEN,
  PRE_MFA_TOKEN: STORAGE_KEYS.PRE_MFA_TOKEN,
  // Aligning generic temp data with the defined signup progress key
  TEMP_DATA: STORAGE_KEYS.SIGNUP_PROGRESS,
} as const;

/**
 * Helper to safely access sessionStorage (handles SSR/Node environments)
 */
const storage = {
  getItem: (key: string): string | null => {
    if (typeof window !== "undefined") {
      return sessionStorage.getItem(key);
    }
    return null;
  },
  setItem: (key: string, value: string) => {
    if (typeof window !== "undefined") {
      sessionStorage.setItem(key, value);
    }
  },
  removeItem: (key: string) => {
    if (typeof window !== "undefined") {
      sessionStorage.removeItem(key);
    }
  },
};

// ----------------------------------------------------------------------
// Pre-Signup Token (Email Verification)
// ----------------------------------------------------------------------

export function setPreSignupToken(token: string) {
  storage.setItem(KEYS.PRE_SIGNUP_TOKEN, token);
}

export function getPreSignupToken(): string | null {
  return storage.getItem(KEYS.PRE_SIGNUP_TOKEN);
}

export function clearPreSignupToken() {
  storage.removeItem(KEYS.PRE_SIGNUP_TOKEN);
}

// ----------------------------------------------------------------------
// Pre-MFA Token (Password Set / Login Challenge)
// ----------------------------------------------------------------------

export function setPreMfaToken(token: string) {
  storage.setItem(KEYS.PRE_MFA_TOKEN, token);
}

export function getPreMfaToken(): string | null {
  return storage.getItem(KEYS.PRE_MFA_TOKEN);
}

export function clearPreMfaToken() {
  storage.removeItem(KEYS.PRE_MFA_TOKEN);
}

// ----------------------------------------------------------------------
// Temporary Data (Form State Persistence)
// ----------------------------------------------------------------------

/**
 * Get all temporary data safely
 */
export function getAllTemporaryData(): Record<string, unknown> {
  const data = storage.getItem(KEYS.TEMP_DATA);
  if (!data) return {};
  try {
    return JSON.parse(data);
  } catch {
    // If JSON is corrupted, clear it to prevent stuck state
    storage.removeItem(KEYS.TEMP_DATA);
    return {};
  }
}

/**
 * Store temporary data (e.g., email, phone during signup)
 * Merges with existing data.
 */
export function setTemporaryData(key: string, value: unknown) {
  const currentData = getAllTemporaryData();
  const newData = { ...currentData, [key]: value };
  storage.setItem(KEYS.TEMP_DATA, JSON.stringify(newData));
}

/**
 * Get temporary data (Typed)
 */
export function getTemporaryData<T = unknown>(key: string): T | undefined {
  const data = getAllTemporaryData();
  return data[key] as T;
}

/**
 * Clear temporary data
 * If key is provided, clears only that key. Otherwise clears all temp data.
 */
export function clearTemporaryData(key?: string) {
  if (key) {
    const currentData = getAllTemporaryData();
    delete currentData[key];
    // If object is empty after delete, remove the key entirely
    if (Object.keys(currentData).length === 0) {
      storage.removeItem(KEYS.TEMP_DATA);
    } else {
      storage.setItem(KEYS.TEMP_DATA, JSON.stringify(currentData));
    }
  } else {
    storage.removeItem(KEYS.TEMP_DATA);
  }
}

// ----------------------------------------------------------------------
// Global Cleanup
// ----------------------------------------------------------------------

/**
 * Clear all auth-related tokens and data
 * Call this on logout or full flow reset.
 */
export function clearAllTokens() {
  storage.removeItem(KEYS.PRE_SIGNUP_TOKEN);
  storage.removeItem(KEYS.PRE_MFA_TOKEN);
  storage.removeItem(KEYS.TEMP_DATA);
}
